/**
 * The trader's layout, held here and saved behind them.
 *
 * ## Optimistic, with the server as the authority on shape
 *
 * A drag has to land instantly — a layout that waits for a round trip before
 * moving is a layout that feels broken — so every edit applies locally first.
 * But the SERVER normalises: it drops a panel this build no longer has,
 * collapses an emptied column, and refuses a ninth workspace. So the response
 * is written back over the local copy rather than discarded, and what the
 * trader ends up with is what was actually stored.
 *
 * The local edit runs through `normaliseWorkspace` too, so the two agree in
 * the ordinary case and the write-back is invisible.
 *
 * ## Why saves are debounced and coalesced
 *
 * Dragging a divider produces a state change per pointer move. Sending each
 * one would be dozens of writes for one gesture, and they would arrive out of
 * order — the layout that lands last wins, and the last one sent is not
 * reliably the last one applied. One save per idle moment, carrying the whole
 * workspace, has neither problem.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Workspace } from '@aegis/core';
import { normaliseWorkspace } from '@aegis/core';
import type { PanelSpecDto, WorkspaceDto, WorkspacesResponse } from '@aegis/api-client';
import { ApiError } from '@aegis/api-client';
import { api } from './api.js';

/** Long enough to swallow a drag, short enough that a tab close does not lose it. */
const SAVE_AFTER_MS = 600;

export interface WorkspaceState {
  readonly all: readonly WorkspaceDto[];
  readonly active: Workspace | null;
  readonly activeId: string | null;
  readonly panels: readonly PanelSpecDto[];
  readonly limit: number;
  readonly loading: boolean;
  /** Set when a save was refused — a full switcher, or the last workspace. */
  readonly refusal: string | null;
  readonly dismissRefusal: () => void;
  /** Apply a pure operation from `@aegis/core` to the active workspace. */
  readonly edit: (fn: (ws: Workspace) => Workspace) => void;
  readonly rename: (name: string) => void;
  readonly switchTo: (id: string) => void;
  readonly create: (name: string, from?: Workspace) => Promise<void>;
  readonly remove: (id: string) => Promise<void>;
}

export function useWorkspaces(): WorkspaceState {
  const [all, setAll] = useState<readonly WorkspaceDto[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [panels, setPanels] = useState<readonly PanelSpecDto[]>([]);
  const [limit, setLimit] = useState(8);
  const [loading, setLoading] = useState(true);
  const [refusal, setRefusal] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The edit waiting to be written. Replaced, never queued — see the header. */
  const pending = useRef<WorkspaceDto | null>(null);

  const absorb = useCallback((r: WorkspacesResponse) => {
    setAll(r.workspaces);
    setActiveId(r.activeWorkspaceId);
    setPanels(r.panels);
    setLimit(r.limit);
  }, []);

  useEffect(() => {
    let live = true;
    void api.workspaces()
      .then((r) => { if (live) absorb(r); })
      .catch(() => { /* The chart still renders from the default; see App. */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [absorb]);

  const flush = useCallback(async () => {
    const ws = pending.current;
    pending.current = null;
    if (!ws) return;
    try {
      const saved = await api.saveWorkspace(ws.id, {
        name: ws.name, columns: ws.columns, detached: ws.detached, position: ws.position,
      });
      // Written back over the local copy: the server is the authority on what
      // a layout MEANS, and a client that kept its own version would drift.
      setAll((prev) => prev.map((w) => (w.id === saved.id ? saved : w)));
    } catch (e) {
      setRefusal(e instanceof ApiError ? e.message : 'That layout could not be saved.');
    }
  }, []);

  const queue = useCallback((ws: WorkspaceDto) => {
    pending.current = ws;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, SAVE_AFTER_MS);
  }, [flush]);

  /*
    A pending edit must survive the tab closing.

    `visibilitychange` rather than `beforeunload`: mobile browsers do not
    reliably fire `beforeunload` at all, and a layout lost because somebody
    switched apps is a layout lost for good.
  */
  useEffect(() => {
    const onHide = (): void => {
      if (document.visibilityState === 'hidden' && pending.current) {
        if (timer.current) clearTimeout(timer.current);
        void flush();
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [flush]);

  const activeDto = all.find((w) => w.id === activeId) ?? all[0] ?? null;
  const active = activeDto ? toWorkspace(activeDto) : null;

  const edit = useCallback((fn: (ws: Workspace) => Workspace) => {
    setAll((prev) => {
      const current = prev.find((w) => w.id === activeId) ?? prev[0];
      if (!current) return prev;
      // Normalised locally as well as on the server, so the write-back that
      // follows is invisible in the ordinary case.
      const next = normaliseWorkspace(fn(toWorkspace(current)));
      const dto: WorkspaceDto = {
        ...current,
        name: next.name,
        columns: next.columns as unknown as WorkspaceDto['columns'],
        detached: [...next.detached],
      };
      queue(dto);
      return prev.map((w) => (w.id === dto.id ? dto : w));
    });
  }, [activeId, queue]);

  const rename = useCallback((name: string) => {
    const trimmed = name.trim().slice(0, 40);
    if (trimmed) edit((ws) => ({ ...ws, name: trimmed }));
  }, [edit]);

  const switchTo = useCallback((id: string) => {
    // Local first: switching layouts must be instant, and the preference is
    // about which one the trader is looking at rather than about the data.
    setActiveId(id);
    void api.setActiveWorkspace(id).catch(() => { /* Reasserted on next load. */ });
  }, []);

  const create = useCallback(async (name: string, from?: Workspace) => {
    const id = `ws_${Date.now().toString(36)}`;
    const base = from ?? { id, name, columns: [], detached: [] };
    try {
      const saved = await api.saveWorkspace(id, {
        name,
        columns: base.columns as unknown as WorkspaceDto['columns'],
        detached: base.detached as unknown as string[],
      });
      setAll((prev) => [...prev, saved]);
      setActiveId(saved.id);
      void api.setActiveWorkspace(saved.id).catch(() => undefined);
    } catch (e) {
      setRefusal(e instanceof ApiError ? e.message : 'That layout could not be created.');
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    try {
      await api.deleteWorkspace(id);
      // Refetched rather than spliced: deleting the ACTIVE one repoints the
      // preference server-side, and guessing which one it landed on here is
      // how a client ends up showing a layout the server does not think is
      // active.
      absorb(await api.workspaces());
    } catch (e) {
      setRefusal(e instanceof ApiError ? e.message : 'That layout could not be deleted.');
    }
  }, [absorb]);

  return {
    all,
    active,
    activeId: activeDto?.id ?? null,
    panels,
    limit,
    loading,
    refusal,
    dismissRefusal: useCallback(() => setRefusal(null), []),
    edit,
    rename,
    switchTo,
    create,
    remove,
  };
}

/**
 * A stored row as the model sees it.
 *
 * The DTO types panel kinds as `string`, because a layout outlives the build
 * that wrote it and the wire contract must be able to carry a kind this build
 * has never heard of. `normaliseWorkspace` is the one place that decides what
 * is real, so the cast here is safe precisely because it is immediately
 * followed by that call.
 */
function toWorkspace(dto: WorkspaceDto): Workspace {
  return normaliseWorkspace({
    id: dto.id,
    name: dto.name,
    columns: dto.columns as unknown as Workspace['columns'],
    detached: dto.detached,
  });
}

/**
 * The symbol one pane is showing, if it chose one.
 *
 * Null means "no opinion" rather than "no symbol" — the caller falls back to
 * the application default. Those are different: a pane the trader deliberately
 * pointed at CL must keep CL when everything else moves, and a pane they never
 * touched should follow.
 */
export function paneSymbolOf(ws: Workspace, paneId: string): string | null {
  for (const column of ws.columns) {
    for (const pane of column.panes) {
      if (pane.id === paneId) return pane.config?.symbol ?? null;
    }
  }
  return null;
}
